import { useState, useEffect, useRef } from "react";
import {
  TextInput,
  Select,
  Stack,
  Group,
  Button,
  Checkbox,
  Text,
  Grid,
  Paper,
  Alert,
  Divider,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCreditCard, IconCheck, IconInfoCircle } from "@tabler/icons-react";
import { notifyError } from "../../utils/notify";
import { useClient } from "../../context/ClientContext";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import useLoyaltyProgram from "../../hooks/useLoyaltyProgram";
import {
  formatPhoneNumber,
  validatePhoneNumber,
  splitPhoneNumber,
} from "../../utils/phoneUtils";
import {
  parseLocalDate,
  formatLocalDate,
  normalizeToLocalMidnight,
} from "../../utils/localDateUtils";
import AddCardModal from "../common/AddCardModal";
import PaymentMethodsSection from "./PaymentMethodsSection";
import formStyles from "./ClientForm.module.css";

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Non-binary", label: "Non-binary" },
  { value: "Other", label: "Other" },
  { value: "Prefer not to say", label: "Prefer not to say" },
];

const pronounOptions = [
  { value: "He/Him", label: "He/Him" },
  { value: "She/Her", label: "She/Her" },
  { value: "They/Them", label: "They/Them" },
  { value: "Other", label: "Other" },
];

const PHONE_REQUIRED_MESSAGE =
  "Phone number is required when text reminders are enabled";

function ClientForm({
  client,
  onClose,
  showAddCardOption = true,
  readOnly = false,
}) {
  const { createClient, updateClient, refreshClientById } = useClient();
  const { businessInfo } = useSalon();
  const { getClientLoyalty, setClientLoyaltyOptIn } = useApp();
  // Loyalty enrollment checkbox (FE1 §4.3): rendered only when the location's
  // loyalty program is enabled. Client-loyalty endpoints take the NUMERIC
  // business_locations.id (apiBusinessLocationId), not the salon uuid.
  const {
    isEnabled: loyaltyEnabled,
    apiBusinessLocationId: loyaltyLocationId,
  } = useLoyaltyProgram();
  const showLoyaltyCheckbox = loyaltyEnabled && !!loyaltyLocationId;
  const [loyaltyOptIn, setLoyaltyOptIn] = useState(false);
  const [initialLoyaltyOptIn, setInitialLoyaltyOptIn] = useState(false);
  // Once the user toggles the checkbox, a late-resolving preload GET must not
  // clobber their choice (it used to revert the tick, so the changed-check
  // skipped the opt-in PUT and enrollment silently never saved).
  const loyaltyTouchedRef = useRef(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    zip_code: "",
    gender: "",
    pronouns: "",
    is_opted_in_reminder: true,
    is_opted_in_marketing: false,
    birthday: null,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [addCardOnFile, setAddCardOnFile] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [createdClient, setCreatedClient] = useState(null);
  const [cardAdded, setCardAdded] = useState(false);

  useEffect(() => {
    if (client) {
      // Combine area_code and phone_number into a single phone field
      const fullPhone =
        client.area_code && client.phone_number
          ? `${client.area_code}${client.phone_number}`
          : "";

      setFormData({
        first_name: client.first_name || "",
        last_name: client.last_name || "",
        phone: fullPhone ? formatPhoneNumber(fullPhone) : "",
        email: client.email || "",
        address_1: client.address_1 || "",
        address_2: client.address_2 || "",
        city: client.city || "",
        state: client.state || "",
        zip_code: client.zip_code || "",
        gender: client.gender || "",
        pronouns: client.pronouns || "",
        is_opted_in_reminder: client.is_opted_in_reminder || false,
        is_opted_in_marketing: client.is_opted_in_marketing || false,
        birthday: parseLocalDate(client.birthday),
      });
    }
  }, [client]);

  // Load the client's current loyalty enrollment for the checkbox default.
  useEffect(() => {
    let cancelled = false;
    if (!showLoyaltyCheckbox || !client?.id) {
      if (!loyaltyTouchedRef.current) setLoyaltyOptIn(false);
      setInitialLoyaltyOptIn(false);
      return undefined;
    }
    getClientLoyalty(client.id, loyaltyLocationId)
      .then((response) => {
        if (cancelled) return;
        const optedIn = response?.account?.opted_in === true;
        // Always seed the baseline (server truth) for changed-detection, but
        // only move the visible checkbox while the user hasn't touched it.
        setInitialLoyaltyOptIn(optedIn);
        if (!loyaltyTouchedRef.current) setLoyaltyOptIn(optedIn);
      })
      .catch((error) => {
        // Backend failure — default to unenrolled.
        console.error("Error loading client loyalty enrollment:", error);
        if (!cancelled) {
          setInitialLoyaltyOptIn(false);
          if (!loyaltyTouchedRef.current) setLoyaltyOptIn(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showLoyaltyCheckbox, client?.id, loyaltyLocationId, getClientLoyalty]);

  /**
   * PUT /clients/{id}/loyalty/opt-in when the checkbox changed
   * ([BE-pending: explicit enrollment] — creates the account eagerly
   * server-side). Best-effort: a failure must not block the client save.
   */
  const maybeSaveLoyaltyOptIn = async (clientId) => {
    if (!showLoyaltyCheckbox || !clientId) return;
    const changed = client
      ? loyaltyOptIn !== initialLoyaltyOptIn
      : loyaltyOptIn;
    if (!changed) return;
    try {
      await setClientLoyaltyOptIn(clientId, {
        business_location_id: loyaltyLocationId,
        opted_in: loyaltyOptIn,
      });
    } catch (error) {
      console.error("Error saving loyalty enrollment:", error);
      notifyError(
        "The client was saved, but the loyalty enrollment change failed. Please try again.",
        { title: "Loyalty enrollment not saved" },
      );
    }
  };

  const isPhoneRequired = formData.is_opted_in_reminder;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (isPhoneRequired && !formData.phone.trim()) {
      newErrors.phone = PHONE_REQUIRED_MESSAGE;
    } else if (formData.phone.trim() && !validatePhoneNumber(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Validate birthday is not in the future
    if (formData.birthday && formData.birthday > new Date()) {
      newErrors.birthday = "Birthday cannot be in the future";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentFormData = { ...formData };

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const clientData = {
        ...currentFormData,
      };

      // Split the phone number into API fields, or omit if empty
      if (currentFormData.phone.trim()) {
        const { areaCode, phoneNumber } = splitPhoneNumber(
          currentFormData.phone,
        );
        clientData.country_code = "1";
        clientData.area_code = areaCode;
        clientData.phone_number = phoneNumber;
      }

      // Remove the combined phone field before sending to API
      delete clientData.phone;

      // Serialize birthday as a local YYYY-MM-DD so it doesn't shift a day
      // when JSON.stringify converts the Date to UTC. Matches the format the
      // client-form portal verifies against (clients.birthday).
      clientData.birthday = currentFormData.birthday
        ? formatLocalDate(currentFormData.birthday)
        : null;

      let response;
      if (client) {
        response = await updateClient(client.id, clientData);
        await maybeSaveLoyaltyOptIn(client.id);
        onClose(response?.client);
      } else {
        response = await createClient(clientData);
        await maybeSaveLoyaltyOptIn(response?.client?.id);

        if (addCardOnFile && response?.client) {
          setCreatedClient(response.client);
          setShowAddCardModal(true);
          setIsLoading(false);
        } else {
          onClose(response?.client);
        }
      }
    } catch (error) {
      console.error("Error saving client:", error);
      setIsLoading(false);
    }
  };

  const handleAddCardSuccess = async () => {
    setCardAdded(true);
    setShowAddCardModal(false);
    try {
      if (createdClient?.id) {
        await refreshClientById(createdClient.id);
      }
    } catch (e) {
      console.error("Error refreshing client after add card:", e);
    }
    setTimeout(() => {
      onClose(createdClient);
    }, 1000);
  };

  const handleSkipCard = () => {
    setShowAddCardModal(false);
    onClose(createdClient);
  };
  const isDisabled = isLoading || readOnly;

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing="md">
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="First Name"
              required={!readOnly}
              value={formData.first_name}
              onChange={(e) =>
                setFormData({ ...formData, first_name: e.target.value })
              }
              error={errors.first_name}
              disabled={isDisabled}
              readOnly={readOnly}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Last Name"
              required={!readOnly}
              value={formData.last_name}
              onChange={(e) =>
                setFormData({ ...formData, last_name: e.target.value })
              }
              error={errors.last_name}
              disabled={isDisabled}
              readOnly={readOnly}
            />
          </Grid.Col>
        </Grid>

        <TextInput
          label="Phone"
          required={!readOnly && isPhoneRequired}
          placeholder="(xxx) xxx-xxxx"
          value={formData.phone}
          onChange={(e) => {
            const formatted = formatPhoneNumber(e.target.value);
            setFormData({ ...formData, phone: formatted });
            if (errors.phone) {
              setErrors({ ...errors, phone: "" });
            }
          }}
          error={errors.phone}
          onBlur={() => {
            if (formData.phone && !validatePhoneNumber(formData.phone)) {
              setErrors({
                ...errors,
                phone: "Please enter a valid 10-digit phone number",
              });
            } else if (isPhoneRequired && !formData.phone.trim()) {
              setErrors({
                ...errors,
                phone: PHONE_REQUIRED_MESSAGE,
              });
            }
          }}
          disabled={isDisabled}
          readOnly={readOnly}
        />

        <TextInput
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          error={errors.email}
          disabled={isDisabled}
          readOnly={readOnly}
        />

        {client && (
          <>
            <TextInput
              label="Address 1"
              placeholder="Street address"
              value={formData.address_1}
              onChange={(e) =>
                setFormData({ ...formData, address_1: e.target.value })
              }
              disabled={isDisabled}
              readOnly={readOnly}
            />

            <TextInput
              label="Address 2"
              placeholder="Apt, suite, unit, etc. (optional)"
              value={formData.address_2}
              onChange={(e) =>
                setFormData({ ...formData, address_2: e.target.value })
              }
              disabled={isDisabled}
              readOnly={readOnly}
            />

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="City"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  disabled={isDisabled}
                  readOnly={readOnly}
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput
                  label="State"
                  value={formData.state}
                  onChange={(e) =>
                    setFormData({ ...formData, state: e.target.value })
                  }
                  disabled={isDisabled}
                  readOnly={readOnly}
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput
                  label="Zip Code"
                  value={formData.zip_code}
                  onChange={(e) =>
                    setFormData({ ...formData, zip_code: e.target.value })
                  }
                  disabled={isDisabled}
                  readOnly={readOnly}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <Select
                  label="Gender"
                  data={genderOptions}
                  value={formData.gender}
                  onChange={(value) =>
                    setFormData({ ...formData, gender: value })
                  }
                  clearable
                  disabled={isDisabled}
                  readOnly={readOnly}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Pronouns"
                  data={pronounOptions}
                  value={formData.pronouns}
                  onChange={(value) =>
                    setFormData({ ...formData, pronouns: value })
                  }
                  clearable
                  disabled={isDisabled}
                  readOnly={readOnly}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <DateInput
                  label="Birthday"
                  placeholder="mm/dd/yyyy"
                  value={formData.birthday}
                  onChange={(date) =>
                    setFormData({
                      ...formData,
                      birthday: normalizeToLocalMidnight(date),
                    })
                  }
                  maxDate={new Date()}
                  clearable
                  error={errors.birthday}
                  disabled={isDisabled}
                  readOnly={readOnly}
                />
              </Grid.Col>
            </Grid>
          </>
        )}

        <Stack spacing="xs">
          <Group gap="xs" align="center" wrap="nowrap">
            <Checkbox
              label="Opt-in to appointment-related text messages"
              checked={formData.is_opted_in_reminder}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData({
                  ...formData,
                  is_opted_in_reminder: checked,
                });
                if (checked && !formData.phone.trim()) {
                  setErrors((prev) => ({
                    ...prev,
                    phone: PHONE_REQUIRED_MESSAGE,
                  }));
                } else if (
                  !checked &&
                  errors.phone === PHONE_REQUIRED_MESSAGE
                ) {
                  setErrors((prev) => ({ ...prev, phone: "" }));
                }
              }}
              disabled={isDisabled}
            />
            {!client && (
              <Tooltip
                multiline
                w={320}
                withArrow
                label={
                  <Stack gap={4}>
                    <Text size="xs">
                      Please read the customer the following message:
                    </Text>
                    <Text size="xs">
                      "Would you like to receive informational text messages
                      about your appointment from Pagoda Labs? There will be
                      about 3 text messages per appointment and will not include
                      marketing content.
                    </Text>
                    <Text size="xs">
                      You can view their privacy policy and terms & conditions
                      on their website, www.getpagoda.com. You can always reply
                      HELP for more information or STOP to opt-out of text
                      messages.
                    </Text>
                    <Text size="xs">Message & data rates may apply."</Text>
                  </Stack>
                }
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Text-reminder consent script"
                >
                  <IconInfoCircle size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          {client && (
            <>
              <Text size="xs" c="dimmed">
                Please read the customer the following message:
              </Text>
              <Text size="xs" c="dimmed">
                "Would you like to receive informational text messages about
                your appointment from Pagoda Labs? There will be about 3 text
                messages per appointment and will not include marketing content.
              </Text>
              <Text size="xs" c="dimmed">
                You can view their privacy policy and terms & conditions on
                their website, www.getpagoda.com. You can always reply HELP for
                more information or STOP to opt-out of text messages.
              </Text>
              <Text size="xs" c="dimmed">
                Message & data rates may apply."
              </Text>
            </>
          )}
        </Stack>

        <Checkbox
          label="Opt-in to marketing communications"
          checked={formData.is_opted_in_marketing}
          onChange={(e) =>
            setFormData({
              ...formData,
              is_opted_in_marketing: e.target.checked,
            })
          }
          disabled={isDisabled}
        />

        {showLoyaltyCheckbox && (
          <Stack gap={4}>
            <Checkbox
              label="Sign up for loyalty program"
              checked={loyaltyOptIn}
              onChange={(e) => {
                loyaltyTouchedRef.current = true;
                setLoyaltyOptIn(e.target.checked);
              }}
              disabled={isDisabled}
            />
            <Text size="xs" c="dimmed" ml={30}>
              Earn points on visits and purchases that can be redeemed for
              rewards at checkout.
            </Text>
          </Stack>
        )}

        {!client &&
          showAddCardOption &&
          !readOnly &&
          businessInfo?.stripe_connect_account_id &&
          businessInfo?.stripe_connect_account_is_onboarded && (
            <Paper
              p="sm"
              withBorder
              className={`${formStyles.cofToggle} ${addCardOnFile ? formStyles.cofToggleActive : ""}`}
            >
              <Checkbox
                label={
                  <Group gap="xs">
                    <IconCreditCard size={16} color="var(--brand-teal)" />
                    <Text size="sm">
                      Add card on file after creating client
                    </Text>
                  </Group>
                }
                checked={addCardOnFile}
                onChange={(e) => setAddCardOnFile(e.target.checked)}
                disabled={isLoading}
                color="teal"
              />
              {addCardOnFile && (
                <Text size="xs" c="dimmed" mt="xs" ml={24}>
                  You'll be prompted to add a card after the client is created.
                </Text>
              )}
            </Paper>
          )}

        {client && (
          <>
            <Divider my="sm" />
            <PaymentMethodsSection
              clientId={client.id}
              clientName={`${client.first_name} ${client.last_name}`}
              readOnly={readOnly}
            />
          </>
        )}

        {cardAdded && (
          <Alert icon={<IconCheck size={16} />} color="teal" variant="light">
            Card added successfully! Closing...
          </Alert>
        )}

        {!readOnly && !client && (
          <Button
            type="submit"
            color="teal"
            loading={isLoading}
            fullWidth
            mt="md"
          >
            Create Client
          </Button>
        )}
        {!readOnly && client && (
          <Group position="right" mt="md">
            <Button variant="default" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" color="teal" loading={isLoading}>
              Update Client
            </Button>
          </Group>
        )}
        {readOnly && (
          <Group position="right" mt="md">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Group>
        )}
      </Stack>

      {createdClient && (
        <AddCardModal
          opened={showAddCardModal}
          onClose={handleSkipCard}
          clientId={createdClient.id}
          salonId={businessInfo?.id || ""}
          stripeAccountId={businessInfo?.stripe_connect_account_id || ""}
          clientName={`${createdClient.first_name} ${createdClient.last_name}`}
          onSuccess={handleAddCardSuccess}
        />
      )}
    </form>
  );
}

export default ClientForm;
